const PROFILE_STORAGE_KEY = "rangerProfile";
export const PROFILE_SYNC_EVENT = "ranger-profile-sync";

const defaultProfile = {
  displayName: "",
  callSign: "",
  pronouns: "",
  timezone: "Central",
  phoneNumber: "",
  email: "",
  bio: "",
  motto: "",
  joinDate: "",
  profilePhotoDataUrl: "",
  backgroundPhotoDataUrl: "",
  awards: [],
  earnedAwards: [],
  vehicles: [],
  equipmentCapabilities: [],
  lastActiveAt: "",
};

const safeParse = (value) => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Unable to parse profile payload", error);
    return null;
  }
};

export const loadProfile = () => {
  if (typeof window === "undefined") {
    return { ...defaultProfile };
  }
  const stored = window.localStorage.getItem(PROFILE_STORAGE_KEY);
  const parsed = safeParse(stored);
  return { ...defaultProfile, ...(parsed || {}) };
};

export const saveProfile = (profile) => {
  if (typeof window === "undefined") {
    return profile;
  }
  const payload = { ...defaultProfile, ...(profile || {}) };
  try {
    // Clear first to free up space (helps if existing data is large/corrupted)
    window.localStorage.removeItem(PROFILE_STORAGE_KEY);
    window.localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new Event(PROFILE_SYNC_EVENT));
  } catch (error) {
    console.error("Unable to persist profile", error);
  }
  return payload;
};

export const updateProfile = (updater) => {
  const current = loadProfile();
  const next = typeof updater === "function" ? updater(current) : { ...current, ...(updater || {}) };
  return saveProfile(next);
};

export const addAward = (award) => {
  return updateProfile((profile) => {
    const nextAward = {
      id: award?.id || `award-${Date.now()}`,
      title: award?.title || "Untitled Award",
      summary: award?.summary || "",
      issuedOn: award?.issuedOn || new Date().toISOString(),
      icon: award?.icon || "medal",
    };
    return { ...profile, awards: [nextAward, ...profile.awards] };
  });
};

export const removeAward = (awardId) => {
  if (!awardId) {
    return loadProfile();
  }
  return updateProfile((profile) => ({
    ...profile,
    awards: profile.awards.filter((award) => award.id !== awardId),
  }));
};

export const PROFILE_IMAGE_LIMIT_MB = 20;

export const fileToDataUrl = async (file) => {
  if (!file) {
    return "";
  }
  if (file.size > PROFILE_IMAGE_LIMIT_MB * 1024 * 1024) {
    throw new Error(`File must be under ${PROFILE_IMAGE_LIMIT_MB} MB`);
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || "");
    reader.onerror = () => reject(new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });
};

export default {
  loadProfile,
  saveProfile,
  updateProfile,
  addAward,
  removeAward,
  fileToDataUrl,
  PROFILE_IMAGE_LIMIT_MB,
};
